/**
 * Review Types - 実評価レビュー画面（ManagerReviewView 由来）
 *
 * frontend/ 側から移植。Phase 2 本番の評価画面（ReviewPage）で使用する
 * 指摘⇔文字起こし連動のためのデータ構造。バックエンド API の実装は別レーン
 * (B系)で進むため、ここでは UI 側が期待する shape だけ定義する。
 */

import { axisDisplayLabel, normalizeReviewDisplayText } from '@/utils/reviewDisplayLabels';

// ---------------------------------------------------------------
// タイムライン表示用の低レベル型（evaluation.ts から最小限のみ抜粋）
// ---------------------------------------------------------------

export type SpeakerKind = 'facilitator' | 'participant';

export type SeverityLevel = 'critical' | 'high' | 'medium' | 'low';

export interface UtteranceFeedback {
  patternId: string;
  patternName: string;
  severity: SeverityLevel;
  reason: string;
  recommendation: string;
  role?: 'primary' | 'supporting';
  feedbackCount?: number;
}

export interface Utterance {
  id: string;
  speaker: SpeakerKind;
  speakerName?: string;
  timestamp: string;
  text: string;
  feedback?: UtteranceFeedback;
  feedbackRole?: 'primary' | 'supporting';
}

export interface TimelineBlock {
  key?: string;
  name: string;
  hideHeader?: boolean;
  startTime: string;
  endTime: string;
  issueCount: number;
  utterances: Utterance[];
}

// ---------------------------------------------------------------
// レビュー API レスポンス（frontend/src/types/review.ts 由来）
// ---------------------------------------------------------------

export interface ReviewSourceRef {
  source_type: string;
  title: string;
  excerpt: string;
  source_uri?: string | null;
  metadata: Record<string, unknown>;
}

export type ReviewItemJudgmentValue = 'OK' | 'NG';

export interface ReviewFeedbackItemJudgment {
  reviewer_user_id: string;
  reviewer_name: string;
  judgment: ReviewItemJudgmentValue;
  comment_body?: string | null;
  reviewed_at?: string | null;
}

export interface ReviewFeedbackAxisDetail {
  feedback_key: string;
  axis: 'script' | 'meaning' | 'advisory';
  status: string;
  reason: string;
  recommendation: string;
}

export interface ReviewAdditionalFeedback {
  id: string;
  reviewer_user_id?: string | null;
  utterance_id: string;
  sequence_index?: number | null;
  timestamp: string;
  speaker: SpeakerKind;
  block_name?: string | null;
  quote: string;
  author_name?: string | null;
  comment_body: string;
  created_at?: string | null;
  can_delete?: boolean;
  can_edit?: boolean;
}

export interface ReviewSessionComment {
  id: string;
  reviewer_user_id: string;
  reviewer_name: string;
  comment_kind: 'primary' | 'followup' | string;
  body: string;
  created_at?: string | null;
}

export interface ReviewFeedbackItem {
  feedback_key: string;
  scenario_id: string;
  section_display_name: string;
  item_id: string;
  item_name: string;
  section_name: string;
  lane: 'must' | 'advisory';
  axis: 'script' | 'meaning' | 'advisory';
  status: string;
  utterance_id?: string | null;
  primary_utterance_id?: string | null;
  supporting_utterance_ids: string[];
  sequence_index?: number | null;
  timestamp: string;
  speaker: SpeakerKind;
  block_name: string;
  quote: string;
  primary_quote: string;
  supporting_quotes: string[];
  reason: string;
  recommendation: string;
  source_refs: ReviewSourceRef[];
  source_feedback_keys?: string[];
  axis_details?: ReviewFeedbackAxisDetail[];
  my_judgment?: ReviewItemJudgmentValue | null;
  my_comment_body?: string | null;
  reviewer_judgments?: ReviewFeedbackItemJudgment[];
}

export interface ReviewTranscriptBlock {
  name: string;
  start_time: string;
  end_time: string;
  utterances: Array<{
    sequence_index: number;
    utterance_id: string;
    speaker: SpeakerKind;
    timestamp: string;
    text: string;
    block_name: string;
  }>;
}

export interface ReviewSessionSummary {
  session_id: string;
  display_name: string;
  coach_name?: string | null;
  client_name?: string | null;
  /** 会議内容サマリー（400字以内）。フィードバック要約ではない。 */
  meeting_summary?: string | null;
  day: string;
  day1_variant?: 'first' | 'repeat' | null;
  date?: string | null;
  legacy_result: string;
  issue_count: number;
  /** real/reference。schemas.py 側は必須だがフロントは optional 扱い。 */
  data_kind?: string;
  /** 評価済みセクション数 */
  reviewed_section_count?: number;
  /** 評価済み scenario 数 */
  reviewed_scenario_count?: number;
  /** session 集約 semantic status */
  semantic_status?: string;
  /** 最新 authoritative 実行日時（ISO8601） */
  latest_evaluated_at?: string;
}

/**
 * schemas.py ReviewLegacyResult に対応。UI では現在未使用だが、
 * ReviewSessionSection の必須フィールドとして受け取るので定義しておく。
 */
export interface ReviewLegacyResult {
  evaluation_result: string;
  issue_count: number;
  participant_ng_count: number;
  checklist_count: number;
  summary?: string | null;
}

/**
 * schemas.py ReviewSemanticResult に対応。UI では items 詳細を使わないため
 * unknown[] で受け取り、必要になった時点で SemanticEvaluationItem を追加する。
 */
export interface ReviewSemanticResult {
  overall_status: string;
  script_axis_summary: string;
  semantic_axis_summary: string;
  overall_summary: string;
  items?: unknown[];
  advisory_items?: unknown[];
}

/**
 * schemas.py ReviewSessionSection に対応。session 内の評価済み section。
 */
export interface ReviewSessionSection {
  scenario: Record<string, unknown>;
  source_blocks?: string[];
  comparison_summary: string;
  utterance_ids?: string[];
  start_sequence_index?: number | null;
  end_sequence_index?: number | null;
  review_feedback_items: ReviewFeedbackItem[];
  legacy: ReviewLegacyResult;
  semantic: ReviewSemanticResult;
}

export interface ReviewSessionDetailResponse {
  session: ReviewSessionSummary;
  transcript_blocks: ReviewTranscriptBlock[];
  session_feedback_items: ReviewFeedbackItem[];
  additional_feedbacks?: ReviewAdditionalFeedback[];
  session_comments?: ReviewSessionComment[];
  my_overall_comment_body?: string | null;
  /** schemas.py 側では default_factory=list。UI では未使用のため optional 扱い。 */
  review_sections?: ReviewSessionSection[];
}

export interface ReviewSessionListResponse {
  total: number;
  sessions: ReviewSessionSummary[];
}

// ---------------------------------------------------------------
// 変換ヘルパ: ReviewTranscriptBlock[] → TimelineBlock[]
// ---------------------------------------------------------------

function toPseudoFeedback(item: ReviewFeedbackItem): UtteranceFeedback {
  const severityMap: Record<string, SeverityLevel> = {
    ACTION: 'medium',
    ISSUE: 'high',
    CHECK: 'medium',
    PASS: 'low',
    GOOD: 'low',
  };
  const axisLabel =
    item.axis_details && item.axis_details.length > 1
      ? item.axis_details
          .map((detail) => axisDisplayLabel(detail.axis))
          .filter((label, index, labels) => labels.indexOf(label) === index)
          .join('・')
      : axisDisplayLabel(item.axis);
  const patternName =
    ['ACTION', 'ISSUE', 'CHECK'].includes(item.status)
      ? normalizeReviewDisplayText(item.item_name)
      : `${normalizeReviewDisplayText(item.item_name)} (${axisLabel})`;
  return {
    patternId: item.item_id,
    patternName,
    severity: severityMap[item.status] ?? 'medium',
    reason: normalizeReviewDisplayText(item.reason),
    recommendation: normalizeReviewDisplayText(item.recommendation),
    role: 'primary',
  };
}

export function reviewTranscriptBlocksToTimeline(
  blocks: ReviewTranscriptBlock[],
  feedbackItems: ReviewFeedbackItem[] = [],
): TimelineBlock[] {
  const primaryFeedbackByUtterance = new Map<string, ReviewFeedbackItem[]>();
  const supportingFeedbackByUtterance = new Map<string, ReviewFeedbackItem[]>();
  for (const item of feedbackItems) {
    const primaryUtteranceId = item.primary_utterance_id || item.utterance_id;
    if (primaryUtteranceId) {
      const existing = primaryFeedbackByUtterance.get(primaryUtteranceId) ?? [];
      existing.push(item);
      primaryFeedbackByUtterance.set(primaryUtteranceId, existing);
    }
    for (const supportingUtteranceId of item.supporting_utterance_ids) {
      if (!supportingUtteranceId || supportingUtteranceId === primaryUtteranceId) continue;
      const existing = supportingFeedbackByUtterance.get(supportingUtteranceId) ?? [];
      existing.push(item);
      supportingFeedbackByUtterance.set(supportingUtteranceId, existing);
    }
  }

  const flatUtterances = blocks
    .flatMap((block, sourceBlockIndex) =>
      block.utterances.map((utterance, sourceUtteranceIndex) => ({
        ...utterance,
        sourceBlockName: utterance.block_name || block.name,
        sourceBlockIndex,
        sourceUtteranceIndex,
      })),
    )
    .sort((a, b) => {
      if (a.sequence_index !== b.sequence_index) return a.sequence_index - b.sequence_index;
      const timestampOrder = a.timestamp.localeCompare(b.timestamp);
      if (timestampOrder !== 0) return timestampOrder;
      if (a.sourceBlockIndex !== b.sourceBlockIndex) return a.sourceBlockIndex - b.sourceBlockIndex;
      return a.sourceUtteranceIndex - b.sourceUtteranceIndex;
    });

  const isOtherBlock = (name: string) => {
    const normalized = name.trim();
    return normalized === '' || normalized === 'その他';
  };

  const timelineUtterances = flatUtterances.map((u) => {
    const primaryList = primaryFeedbackByUtterance.get(u.utterance_id);
    const supportingList = supportingFeedbackByUtterance.get(u.utterance_id);
    const feedback = primaryList
      ? { ...toPseudoFeedback(primaryList[0]!), feedbackCount: primaryList.length }
      : supportingList
        ? {
            ...toPseudoFeedback(supportingList[0]!),
            role: 'supporting' as const,
            feedbackCount: supportingList.length,
          }
        : undefined;
    return {
      id: u.utterance_id,
      speaker: u.speaker,
      timestamp: u.timestamp,
      text: u.text,
      feedback,
      feedbackRole: primaryList
        ? ('primary' as const)
        : supportingList
          ? ('supporting' as const)
          : undefined,
      blockName: u.sourceBlockName,
    };
  });

  const timelineBlocks: TimelineBlock[] = [];
  for (const utterance of timelineUtterances) {
    const blockName = utterance.blockName || 'その他';
    const lastBlock = timelineBlocks[timelineBlocks.length - 1];
    if (lastBlock && lastBlock.name === blockName) {
      lastBlock.endTime = utterance.timestamp;
      if (utterance.feedback) lastBlock.issueCount += 1;
      lastBlock.utterances.push(utterance);
      continue;
    }

    timelineBlocks.push({
      key: `${timelineBlocks.length}:${blockName}:${utterance.id}`,
      name: blockName,
      hideHeader: isOtherBlock(blockName),
      startTime: utterance.timestamp,
      endTime: utterance.timestamp,
      issueCount: utterance.feedback ? 1 : 0,
      utterances: [utterance],
    });
  }

  return timelineBlocks;
}
